import { Link } from 'react-router';
import { Tag } from '@/components/Tag';
import { daysUntil, money, num, qty, shortDate } from '@/lib/format';
import { awaitingStatuses, purchaseStatus } from './status';
import type { ChipItem } from './ListKit';
import type { PackagingOption, PurchaseOrder, PurchaseOrderLine, PurchaseStatus } from './types';
import s from './PurchaseCards.module.css';

/* ------------------------------ 口径（照老系统采购单列表的列） ------------------------------ */

/** 采购数量 / 已入库 / 未入库 / 两种货值。货值要单价，单价被裁掉（undefined）时返回 undefined */
export function poTotals(lines: PurchaseOrderLine[]) {
  let ordered = 0;
  let received = 0;
  let pending = 0;
  let receivedValue = 0;
  let pendingValue = 0;
  const priced = lines.length > 0 && lines.every((l) => l.unitCost !== undefined);
  for (const l of lines) {
    const q = num(l.quantity);
    const r = num(l.receivedQuantity);
    const p = Math.max(q - r, 0);
    ordered += q;
    received += r;
    pending += p;
    receivedValue += r * num(l.unitCost);
    pendingValue += p * num(l.unitCost);
  }
  return { ordered, received, pending, receivedValue: priced ? receivedValue : undefined, pendingValue: priced ? pendingValue : undefined };
}

export function packagingName(p?: PackagingOption | null) {
  return p ? p.packageName || p.packageMethod || p.description || '包装方案' : '';
}

/** 「4 箱 × 100 个/箱」；没按件数下单时退回包装方案名 */
export function packText(l: PurchaseOrderLine) {
  const upp = num(l.unitsPerPackage ?? l.packagingOption?.unitsPerPackage);
  const pq = num(l.packageQuantity);
  const unit = l.packagingOption?.packageUnit || '件';
  if (pq > 0 && upp > 0) return `${qty(pq)} ${unit} × ${qty(upp)} 个/${unit}`;
  return packagingName(l.packagingOption);
}

/* ------------------------------ 到货倒计时 ------------------------------ */

/** 只对还在等货的单子（已下单 / 部分入库）提示：逾期、今天红，≤3 天琥珀 */
export function Arrive({ po }: { po: Pick<PurchaseOrder, 'status' | 'expectedArrivalDate'> }) {
  if (!awaitingStatuses.includes(po.status)) return null;
  const d = daysUntil(po.expectedArrivalDate);
  if (d === null) return null;
  if (d < 0) return <span className={`${s.due} ${s.bad}`}>逾期 {-d} 天</span>;
  if (d === 0) return <span className={`${s.due} ${s.bad}`}>今日到货</span>;
  if (d <= 3) return <span className={`${s.due} ${s.warn}`}>剩 {d} 天</span>;
  return <span className={s.muted}>剩 {d} 天</span>;
}

export function Progress({ received, ordered, label, stateText }: { received: number; ordered: number; label?: boolean; stateText?: string }) {
  const pct = ordered > 0 ? Math.min(100, Math.max(0, (received / ordered) * 100)) : 0;
  return (
    <div className={s.prog}>
      <div className={s.bar} role="progressbar" aria-valuemin={0} aria-valuemax={ordered} aria-valuenow={received} aria-label="入库进度">
        <i style={{ width: `${pct}%` }} />
      </div>
      {label && <span className="tnum">{qty(received)} / {qty(ordered)} {stateText || '已入库'}</span>}
    </div>
  );
}

export function purchaseProgressText(po: Pick<PurchaseOrder, 'expectedArrivalDate' | 'lines'>) {
  const t = poTotals(po.lines);
  const remainingDays = daysUntil(po.expectedArrivalDate);
  return t.pending <= 0 ? '已入库'
    : remainingDays !== null && remainingDays < 0 ? '逾期未齐'
      : t.received <= 0 ? (remainingDays !== null && remainingDays > 0 ? '未到货' : '未入库')
        : Math.abs(t.received * 2 - t.ordered) < 0.0001 ? '已入一半' : '部分入库';
}

/* ------------------------------ 采购单卡（P2） ------------------------------ */

export function PurchaseCard({ po, to, selected }: { po: PurchaseOrder; to: string; selected?: boolean }) {
  const st = purchaseStatus[po.status] || { label: po.status, tone: 'none' as const };
  const t = poTotals(po.lines);
  const progressText = purchaseProgressText(po);
  return (
    <Link to={to} className={[s.card, selected && s.sel].filter(Boolean).join(' ')}>
      <div className={s.h}>
        <b className="tnum">{po.purchaseNo}</b>
        <Tag tone={st.tone} sm>{st.label}</Tag>
      </div>
      <div className={s.c}>{po.supplier.name}</div>
      <div className={s.sub}><i>仓库</i>{po.warehouse.name}{po.orderDate && <> · <span className="tnum">{shortDate(po.orderDate)}</span> 下单</>}</div>
      {po.lines.length > 0 && (
        <ul className={s.l}>
          {po.lines.slice(0, 2).map((l) => {
            const r = num(l.receivedQuantity);
            const q = num(l.quantity);
            return (
              <li key={l.id}>
                <span>{l.product.name}</span>
                <b className="tnum">{r > 0 && r < q ? `已入 ${qty(r)} / ${qty(q)}` : `${qty(q)} 个`}</b>
              </li>
            );
          })}
          {po.lines.length > 2 && <li className={s.more}>+{po.lines.length - 2} 项</li>}
        </ul>
      )}
      <div className={s.d}>
        <span><i>到货</i><b className="tnum">{shortDate(po.expectedArrivalDate)}</b></span>
        <Arrive po={po} />
        {po.totalAmount !== undefined && <span className={s.amt}>{money(po.totalAmount)}</span>}
      </div>
      {po.status !== 'CANCELLED' && po.status !== 'DRAFT' && <Progress received={t.received} ordered={t.ordered} label stateText={progressText} />}
    </Link>
  );
}

/* ------------------------------ 采购的视图 chips（采购单 + 采购建议两页共用） ------------------------------ */

export const poViews: { key: 'all' | 'ready' | PurchaseStatus; label: string }[] = [
  { key: 'ready', label: '已下单待到货' },
  { key: 'PARTIALLY_RECEIVED', label: '部分入库' },
  { key: 'DRAFT', label: '草稿' },
  { key: 'RECEIVED', label: '已入库' },
  { key: 'CANCELLED', label: '已取消' },
  { key: 'all', label: '全部采购单' },
];

/** 「采购建议」和采购单各状态排在同一排 chips（P1/P2）；pick 由页面决定是改参数还是跳页 */
export function purchaseChips(opts: { active: string; counts: Partial<Record<string, number>>; suggestCount?: number; canSuggest: boolean; canPO: boolean; pick: (key: string) => void }): ChipItem[] {
  const out: ChipItem[] = [];
  if (opts.canSuggest) out.push({ key: 'suggest', label: '待下单建议', count: opts.suggestCount, active: opts.active === 'suggest', onClick: () => opts.pick('suggest') });
  if (opts.canPO) {
    for (const v of poViews) {
      const n = v.key === 'ready' ? (opts.counts.ORDERED ?? 0) + (opts.counts.PARTIALLY_RECEIVED ?? 0) : opts.counts[v.key];
      // 草稿 / 已取消 没有单子时不占位置
      if ((v.key === 'DRAFT' || v.key === 'CANCELLED') && !n && opts.active !== v.key) continue;
      out.push({ key: v.key, label: v.label, count: n, active: opts.active === v.key, onClick: () => opts.pick(v.key) });
    }
  }
  return out;
}
